import { useState } from "react";
import { useTranslation } from "react-i18next";
import useAuth from "../useAuth";
import { isFreePlan, usePermissions } from "../../utils/permissions";
import { usePaymentVerification } from "../usePaymentVerification";
import { useApplyPromoCode, useGetTrialStatus } from "./index";

export const TRIAL_PROMO_CODE = "SUM-TRIAL-14";

export const useTrialEligibility = (): { eligible: boolean } => {
  const { user, customer_identification } = useAuth();
  const permissions = usePermissions(user);

  const { data: trialStatus } = useGetTrialStatus(
    customer_identification,
    user.entity_identification
  );

  const eligible =
    !permissions.isRoleGoverned() &&
    isFreePlan(user.customer_top_plan) &&
    Boolean(trialStatus?.data) &&
    !trialStatus?.data?.trial_info;

  return { eligible };
};

export const useActivateTrial = (): {
  activate: () => Promise<boolean>;
  isActivating: boolean;
  error: string | null;
} => {
  const { t } = useTranslation();
  const { user, customer_identification } = useAuth();
  const { mutateAsync: applyPromoCode } = useApplyPromoCode();
  const { forcePaymentCheck } = usePaymentVerification();

  const [isActivating, setIsActivating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activate = async (): Promise<boolean> => {
    setError(null);
    setIsActivating(true);
    try {
      await applyPromoCode({
        customerIdentification: customer_identification,
        entityIdentification: user.entity_identification,
        promoCode: TRIAL_PROMO_CODE,
      });
      await forcePaymentCheck();
      return true;
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          (t("dashboard.promoCode.errorInvalid") as string)
      );
      return false;
    } finally {
      setIsActivating(false);
    }
  };

  return { activate, isActivating, error };
};
